import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { AudienceTag } from "../components/Tags";
import { CTASection } from "../components/CTASection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAMS = [
  {
    n: "01",
    title: "1-on-1 Tutoring & Lessons",
    who: "INDIVIDUALS · ONLINE OR IN PERSON",
    body: "Private lessons at your pace — conversation practice, exam preparation, business fluency, or Japanese for daily life in Canada. Every lesson is built around your goals and your calendar.",
  },
  {
    n: "02",
    title: "Language Courses & Programs",
    who: "INDIVIDUALS & TEAMS · 8–12 WEEK TERMS",
    body: "Structured courses from beginner to professional fluency, for individuals and for teams inside organizations. Small groups, practical content, real situations from week one.",
  },
  {
    n: "03",
    title: "ESL Programs",
    who: "NEWCOMERS, EMPLOYEES & FAMILIES",
    body: "English-as-a-second-language programs for people building a life in Canada — workplace English, everyday communication, and the confidence to speak first.",
  },
];

const SESSIONS = [
  { n: "01", title: "Cross-Border M&A Workshops", body: "Tailored sessions for leadership teams preparing a Japan–Canada acquisition: diligence culture, negotiation rhythm, and the unwritten rules of both boardrooms." },
  { n: "02", title: "Joint Venture Alignment", body: "Working sessions that align partner executives on decision rights, communication norms, and escalation — before the first disagreement, not after." },
  { n: "03", title: "Executive Relocation Briefings", body: "Private preparation for executives and their families moving between Tokyo and Toronto: business protocol, daily life, and the first ninety days." },
];

const PARTNERSHIPS = [
  ["Academic Institution Onboarding", "Getting enrolled, oriented, and set up at schools, colleges, and universities — admissions communication, documentation, and arrival."],
  ["Institutional Partnerships", "Bridging universities, colleges, and schools across Japan and Canada — from first introduction to signed agreement."],
  ["Academic Exchange Support", "Logistics, language, and liaison for exchange students and faculty, in both directions."],
  ["Agent & Boarding Services", "Careful placement, guardianship, and boarding arrangements for students studying abroad."],
  ["Support for Students Attending School Here", "Day-to-day guidance for students studying in Canada — and for the families sending them."],
];

export default function EducationAcademics() {
  const [seminars, setSeminars] = useState([]);
  useEffect(() => {
    axios.get(`${API}/seminars`).then((r) => setSeminars(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="education-page" className="bg-slatebg pt-16">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-16">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <Overline>EDUCATION & ACADEMICS</Overline>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy">
                Learn the language. Learn the culture. Land ready.
              </h1>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-charcoal/75 max-w-2xl">
                Courses, tutoring, ESL, executive sessions, seminars, and academic partnerships —
                taught by people who work between Japan and Canada every day.
              </p>
              <div className="mt-6 flex gap-2">
                <AudienceTag audience="business" testId="edu-tag-business" />
                <AudienceTag audience="individual" testId="edu-tag-individual" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-sm border border-slateborder">
                <img
                  src="https://images.pexels.com/photos/1792927/pexels-photo-1792927.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="A bridge crossing a wide river"
                  data-testid="edu-header-image"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Courses & lessons */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-20">
        <Reveal>
          <div className="border-t-2 border-navy pt-5">
            <span className="font-mono text-xs tracking-[0.22em] text-coral-dark uppercase">Courses & Lessons</span>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.07}>
              <div data-testid={`program-${p.n}`} className="bg-cream border border-slateborder rounded-sm p-8 h-full flex flex-col">
                <span className="font-mono text-sm tracking-widest text-coral-dark">{p.n}</span>
                <h3 className="mt-3 font-serif text-2xl text-navy">{p.title}</h3>
                <p className="mt-2 text-[10px] font-mono tracking-[0.18em] text-charcoal/50">{p.who}</p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/70 flex-1">{p.body}</p>
                <Link to="/contact" data-testid={`program-${p.n}-cta`} className="group mt-6 inline-flex items-center gap-1.5 text-[11px] font-mono tracking-wider uppercase text-coral-dark hover:text-coral transition-colors duration-300">
                  Contact us <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Executive private sessions */}
      <section data-testid="edu-private-sessions" className="bg-navy text-cream-surface">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
          <Reveal>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-px w-10 bg-coral-light" />
              <p className="text-xs uppercase tracking-[0.24em] font-mono text-coral-light">EXECUTIVE PRIVATE SESSIONS</p>
              <span className="h-px w-10 bg-coral-light" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight max-w-2xl">
              On-site workshops for leadership teams, built around your real situation.
            </h2>
            <p className="mt-5 text-sm sm:text-base text-[#B7C6E2] max-w-2xl leading-relaxed">
              No webinars, no recordings. A senior practitioner comes to your boardroom — confidentially, in English, Japanese, or both. Half or full day.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-navy-border border border-navy-border rounded-sm overflow-hidden">
            {SESSIONS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08} className="bg-navy">
                <div data-testid={`session-${s.n}`} className="p-8 sm:p-10 h-full flex flex-col transition-colors duration-300 hover:bg-navy-surface">
                  <span className="font-mono text-sm tracking-widest text-coral-light">{s.n}</span>
                  <h3 className="mt-3 font-serif text-2xl text-cream-surface">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#B7C6E2] flex-1">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/contact" data-testid="sessions-book" className="group mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-semibold px-7 py-3.5 rounded-full transition-colors duration-300">
              Book a consultation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Seminar calendar */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
        <Reveal>
          <div className="border-t-2 border-navy pt-5">
            <span className="font-mono text-xs tracking-[0.22em] text-coral-dark uppercase">Upcoming Seminars</span>
          </div>
        </Reveal>
        <div className="mt-10 divide-y divide-slateborder border-y border-slateborder">
          {seminars.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05}>
              <div data-testid={`edu-seminar-${s.id}`} className="py-7 grid grid-cols-1 md:grid-cols-12 gap-3 items-baseline">
                <p className="md:col-span-2 font-mono text-xs text-coral-dark">{s.date}</p>
                <div className="md:col-span-7">
                  <h3 className="font-serif text-xl sm:text-2xl text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/70 leading-relaxed max-w-xl">{s.description}</p>
                </div>
                <p className="md:col-span-2 font-mono text-[11px] tracking-widest text-charcoal/50 uppercase">{s.location}</p>
                <div className="md:col-span-1 md:text-right">
                  <Link to="/insights#seminars" data-testid={`edu-seminar-register-${s.id}`} className="group inline-flex items-center gap-1.5 text-[11px] font-mono tracking-wider uppercase text-coral-dark hover:text-coral transition-colors duration-300">
                    Register <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Academic partnerships */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-20 sm:pb-28">
        <Reveal>
          <div className="border-t-2 border-navy pt-5">
            <span className="font-mono text-xs tracking-[0.22em] text-coral-dark uppercase">Academic Partnerships & Student Support</span>
          </div>
        </Reveal>
        <div className="mt-10">
          {PARTNERSHIPS.map(([title, body], i) => (
            <Reveal key={title} delay={i * 0.04}>
              <div data-testid={`partnership-${i}`} className="border-t border-slateborder py-5 grid grid-cols-1 sm:grid-cols-12 gap-2 items-baseline">
                <p className="sm:col-span-4 text-base font-medium text-navy">{title}</p>
                <p className="sm:col-span-6 text-sm leading-relaxed text-charcoal/70">{body}</p>
                <div className="sm:col-span-2 sm:text-right">
                  <Link to="/contact" data-testid={`partnership-${i}-cta`} className="group inline-flex items-center gap-1.5 text-[11px] font-mono tracking-wider uppercase text-coral-dark hover:text-coral transition-colors duration-300">
                    Contact us <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
